import React, { memo, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { validators } from 'investira.sdk';
import {
  Stack,
  LoadingButton,
  Icon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import services from '../../../services';
import { AVALIACAO_SESSOES, AVALIACAO_SESSOES_QUESTOES, AVALIACAO_QUESTOES, AVALIACAO_MODELO } from '../../../enums';
import AvalicoesSaveStatus from '../_components/AvalicoesSaveStatus';
import SessaoNavProgress from '../_components/SessaoNavProgress';
import AvaliacoesEditSugestoes from './AvaliacoesEditSugestoes';
import AvaliacoesEditQuestoes from './AvaliacoesEditQuestoes';
import AvaliacoesProgresso from '../_components/AvaliacoesProgresso';

const AvaliacoesEdit = memo(props => {
  const { state, actions } = useAvaliacoesContext();
  const {
    sessoes,
    gestor,
    activeCategoryId,
    activeQuestionId,
    activeAliasQuestionId,
    selectedModelo
  } = state;
  const [questoesAvaliadas, setQuestoesAvaliadas] = useState({});
  const [isProcessarDialogOpen, setIsProcessarDialogOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const xModeloSelecionado = AVALIACAO_MODELO[selectedModelo];
  const xModeloLabel = xModeloSelecionado?.label;
  const xModeloSessoes = xModeloSelecionado?.sessoes || Object.keys(sessoes);

  const xQuestoesDisponiveis = useMemo(() => {
    return Object.values(AVALIACAO_QUESTOES).filter(xQuestao => {
      if (!xModeloLabel) return true;
      return (xQuestao?.modelos || []).includes(xModeloLabel);
    });
  }, [xModeloLabel]);

  const xQuestoesDisponiveisIds = useMemo(
    () => new Set(xQuestoesDisponiveis.map(xQuestao => xQuestao.id)),
    [xQuestoesDisponiveis]
  );

  const xQuestoesPorSessao = useMemo(() => {
    return Object.fromEntries(
      Object.values(sessoes).map(xSessao => {
        const xQuestoesSessao = (AVALIACAO_SESSOES_QUESTOES[xSessao.id]?.questoes || []).filter(
          xQuestao => xQuestoesDisponiveisIds.has(xQuestao.id)
        );

        return [
          xSessao.id,
          {
            ...(AVALIACAO_SESSOES_QUESTOES[xSessao.id] || {}),
            questoes: xQuestoesSessao
          }
        ];
      })
    );
  }, [sessoes, xQuestoesDisponiveisIds]);

  const xSessoesDisponiveis = useMemo(() => {
    return Object.fromEntries(
      Object.values(sessoes)
        .filter(xSessao => xModeloSessoes.includes(xSessao.id))
        .map(xSessao => [xSessao.id, xSessao])
    );
  }, [sessoes, xModeloSessoes]);

  const xSessoesDisponiveisIds = useMemo(
    () => new Set(Object.keys(xSessoesDisponiveis)),
    [xSessoesDisponiveis]
  );
  const xSessoesQuestoes = xQuestoesPorSessao[activeCategoryId] || { questoes: [] };
  const xQuestoesSessaoAtiva = xSessoesQuestoes.questoes || [];
  const xQuestoesSize = xQuestoesDisponiveis.length;
  const xQuestoesIds = xQuestoesDisponiveisIds;

  const xRespondidas = 0;

  const xRevisadas = useMemo(() => {
    return Object.entries(questoesAvaliadas || {}).reduce((pCount, [pQuestionId, pResposta]) => {
      if (!xQuestoesIds.has(Number(pQuestionId))) return pCount;
      return pResposta === true ? pCount + 1 : pCount;
    }, 0);
  }, [questoesAvaliadas, xQuestoesIds]);

  const xSessoesProgress = useMemo(() => {
    return Object.fromEntries(
      Object.values(xSessoesDisponiveis).map(xSessao => {
        const xQuestoesSessao = xQuestoesPorSessao[xSessao.id]?.questoes || [];
        const xTotalSessao = xQuestoesSessao.length;
        const xRevisadasSessao = xQuestoesSessao.reduce((pCount, pQuestao) => {
          return questoesAvaliadas?.[pQuestao.id] === true ? pCount + 1 : pCount;
        }, 0);

        const xProgression = xTotalSessao ? (xRevisadasSessao / xTotalSessao) * 100 : 0;

        return [
          xSessao.id,
          {
            ...xSessao,
            progression: xProgression,
            completed: xTotalSessao > 0 && xRevisadasSessao >= xTotalSessao
          }
        ];
      })
    );
  }, [xSessoesDisponiveis, xQuestoesPorSessao, questoesAvaliadas]);

  const xActiveAliasQuestionId = useMemo(() => {
    const xSessaoAtual = sessoes[activeCategoryId];
    if (!xSessaoAtual) return activeAliasQuestionId;

    const xQuestionIndex = xQuestoesSessaoAtiva.findIndex(
      xQuestao => xQuestao.id === activeQuestionId
    );
    if (xQuestionIndex === -1) return activeAliasQuestionId;

    return `${xSessaoAtual.sessao}.${xQuestionIndex + 1}`;
  }, [sessoes, activeCategoryId, activeQuestionId, activeAliasQuestionId, xQuestoesSessaoAtiva]);

  useEffect(() => {
    const xPrimeiraSessaoDisponivel = Object.keys(xSessoesDisponiveis)[0];
    if (!xPrimeiraSessaoDisponivel) return;

    if (!xSessoesDisponiveisIds.has(activeCategoryId)) {
      actions.handleChangeActiveSessaoId(xPrimeiraSessaoDisponivel);
      const xPrimeiraQuestao = xQuestoesPorSessao[xPrimeiraSessaoDisponivel]?.questoes?.[0];
      if (xPrimeiraQuestao) {
        actions.handleChangeActiveQuestionId(xPrimeiraQuestao.id);
      }
      return;
    }

    const xQuestoesSessaoAtual = xQuestoesPorSessao[activeCategoryId]?.questoes || [];
    const xQuestaoAtivaDisponivel = xQuestoesSessaoAtual.some(
      xQuestao => xQuestao.id === activeQuestionId
    );

    if (!xQuestaoAtivaDisponivel && xQuestoesSessaoAtual[0]) {
      actions.handleChangeActiveQuestionId(xQuestoesSessaoAtual[0].id);
    }
  }, [
    actions,
    activeCategoryId,
    activeQuestionId,
    xSessoesDisponiveis,
    xSessoesDisponiveisIds,
    xQuestoesPorSessao
  ]);

  const handleQuestionChange = (pQuestionId, pData = {}) => {
    setQuestoesAvaliadas(pPrev => ({
      ...pPrev,
      [pQuestionId]: Boolean(pData?.avaliado)
    }));
  };

  const handleSelectSessao = (_pEvent, pSessao, pIndex) => {
    !validators.isNull(pSessao) && actions.handleChangeActiveSessaoId(pSessao.id);

    const xQuestoes = xQuestoesPorSessao[pSessao.id]?.questoes;
    if (!validators.isEmpty(xQuestoes)) {
      const xFirstSessaoQuestionId = xQuestoes[0].id;
      actions.handleChangeActiveQuestionId(xFirstSessaoQuestionId);
    } else {
      // TODO: Adiconar feedback para o usuário
      console.log('Falha no carregamento das questões');
    }
  };

  const handleProcessarAvaliacao = () => {
    setIsProcessarDialogOpen(true);
  };

  const handleCloseProcessarDialog = () => {
    setIsProcessarDialogOpen(false);
  };

  const handleConfirmProcessarAvaliacao = async () => {
    setIsProcessarDialogOpen(false);

    try {
      const xModeloLabel = AVALIACAO_MODELO[selectedModelo]?.label;

      const xQuestoesFiltradas = Object.values(AVALIACAO_QUESTOES).filter(xQuestao => {
        if (!xModeloLabel) return true;
        return (xQuestao?.modelos || []).includes(xModeloLabel);
      });

      const xRespostas = xQuestoesFiltradas.map(xQuestao => {
        const xJustificativa = Math.random() > 0.4 ? 'Alteração baseada na análise dos documentos enviados pela gestora.' : null;

        return {
          id: xQuestao.id,
          questao: xQuestao.questao,
          sessao: xQuestao.sessao,
          sessao_id: xQuestao.sessao_id,
          sessao_label: xQuestao.sessao_label,
          slug: xQuestao.slug,
          peso: xQuestao.peso,
          resposta_gestor: 'Resposta registrada pelo gestor conforme documentação apresentada.',
          nota_gestor: Math.round(Math.random() * 3 * 10) / 10,
          nota: Math.round(Math.random() * 3 * 10) / 10,
          houve_alteracao_nota: !!xJustificativa,
          justificativa: xJustificativa
        };
      });

      const xNotaQualitativa = Math.round(Math.random() * 3 * 10) / 10;
      const xNotaQuantitativa = Math.round(Math.random() * 3 * 10) / 10;
      const xScoreGeral = Math.round((xNotaQualitativa * 0.7 + xNotaQuantitativa * 0.3) * 10) / 10;

      const xSessoesResult = Object.values(AVALIACAO_SESSOES).map(xSessao => ({
        id: xSessao.id,
        label: xSessao.label,
        sessao: xSessao.sessao,
        descricao: xSessao.descricao,
        nota: Math.round(Math.random() * 3 * 10) / 10,
        nota_peso: Math.round(Math.random() * 3 * 10) / 10
      }));

      const xPayload = {
        avaliacao_id: id,
        gestor,
        score_geral: xScoreGeral,
        nota: xScoreGeral,
        nota_qualitativa: xNotaQualitativa,
        nota_quantitativa: xNotaQuantitativa,
        sessoes: xSessoesResult,
        respostas: xRespostas,
        usuario: {
          nome: 'Fulano Silva',
          cargo: 'Gerente de Risco'
        },
        resumo: 'A gestora apresenta estrutura de governança compatível com as exigências regulatórias vigentes, destacando-se nos critérios de potencialidade fiduciária.',
        proxima_avaliacao: 'Fevereiro 2027',
        justificativas: xRespostas.filter(r => r.justificativa).length,
        created: new Date().toISOString()
      };

      const rRes = await services.mockAvaliacoes.resultadosRepo.upsertResultado(xPayload);

      if (!rRes.ok) {
        console.error('Erro ao persistir resultado:', rRes.error);
      }

      // Atualiza status da avaliação
      const xAvaliacaoRes = await services.mockAvaliacoes.avaliacoesRepo.getByAvaliacaoId(id);
      if (xAvaliacaoRes.ok && xAvaliacaoRes.data?.id) {
        await services.mockAvaliacoes.avaliacoesRepo.update(xAvaliacaoRes.data.id, {
          status: 'Concluído pelo Gestor',
          score: xScoreGeral
        });
      }
    } catch (error) {
      console.error('Erro ao processar avaliação:', error);
    }

    navigate(`${location.pathname}/resultado`);
  };

  return (
    <PageTemplate
      title={gestor.pessoa_fantasia || 'EXEMPLO'}
      subtitle={'Avaliação Gestor'}
      maxWidth={false}
      headerRenderActions={
        <Stack direction="row" spacing={2} alignItems="center">
          <LoadingButton
            variant="outlined"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            type="submit"
            disabled={props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Salvar Rascunho
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="execute" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            onClick={handleProcessarAvaliacao}
            type="button"
            disabled={props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Processar Avaliação
          </LoadingButton>
        </Stack>
      }>
      <Stack direction="row" gap={6}>
        <Stack
          id="sessoes"
          direction="column"
          width="25%"
          gap={2}
          sx={{
            position: 'sticky',
            top: '-24px',
            alignSelf: 'flex-start',
            maxHeight: 'calc(80vh - 36px)',
            overflowY: 'auto'
          }}>
          <AvaliacoesProgresso
            qtdQuestoes={xQuestoesSize}
            respondidas={xRespondidas}
            revisadas={xRevisadas}
          />
          <Divider />
          <SessaoNavProgress
            sessoes={xSessoesProgress}
            activeCategoryId={activeCategoryId}
            onClick={handleSelectSessao}
          />
        </Stack>
        <Stack direction="column" flexGrow="1" minWidth="40%" maxWidth="40%" gap={2}>
          <Stack direction="row" justifyContent="end">
            <AvalicoesSaveStatus status={0} />
          </Stack>
          <Divider />
          <AvaliacoesEditQuestoes
            categorias={sessoes}
            activeCategoryId={activeCategoryId}
            activeQuestionId={activeQuestionId}
            onSelect={actions.handleChangeActiveQuestionId}
            questoes={xQuestoesSessaoAtiva}
            respostas={questoesAvaliadas}
            onQuestionChange={handleQuestionChange}
          />
        </Stack>
        <Stack
          id="sugestoes"
          direction="column"
          maxWidth="35%"
          minWidth="35%"
          sx={{
            position: 'sticky',
            top: '-24px',
            alignSelf: 'flex-start',
            maxHeight: 'calc(80vh - 32px)',
            overflowY: 'auto'
          }}>
          <AvaliacoesEditSugestoes
            activeCategoryId={activeCategoryId}
            activeQuestionId={activeQuestionId}
            activeAliasQuestionId={xActiveAliasQuestionId}
          />
        </Stack>
      </Stack>
      <Dialog
        open={isProcessarDialogOpen}
        onClose={handleCloseProcessarDialog}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Processar avaliação?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revisão concluída? Após confirmar, não será mais possível editar a avaliação.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProcessarDialog} variant="outlined">
            Ainda, não finalizei
          </Button>
          <Button onClick={handleConfirmProcessarAvaliacao} variant="contained" autoFocus>
            Processar
          </Button>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
});

AvaliacoesEdit.displayName = 'AvaliacoesEdit';

export default AvaliacoesEdit;
